// Shared projectile ballistics (MOB-5). PURE (ENG-1).
//
// The per-tick integration + block/entity hit resolution lives in
// EntitySystem.tickProjectiles (it needs the world + player list); this module
// holds the pure aiming and range math so it is unit-testable in isolation.
import { PROJECTILE } from '../../config';

export interface Vec3 { x: number; y: number; z: number; }

/**
 * Launch velocity to lob a projectile from the origin toward (dx,dy,dz) at
 * `speed`, with a gravity-compensating upward lead proportional to the ground
 * distance (skeleton aiming, MOB-5).
 */
export function solveAim(dx: number, dy: number, dz: number, speed: number): Vec3 {
  const flat = Math.hypot(dx, dz) || 1e-4;
  const len = Math.hypot(dx, dy, dz) || 1e-4;
  const lead = flat * 0.10; // rises with distance to counter the arrow's drop
  return {
    x: (dx / len) * speed,
    y: (dy / len) * speed + lead * PROJECTILE.ARROW_GRAVITY,
    z: (dz / len) * speed,
  };
}

/**
 * Horizontal distance an arrow travels before returning to its launch height,
 * integrating the same gravity/drag the live projectile uses. Used by the
 * ballistic-range table test (45° ≈ documented blocks).
 */
export function ballisticRange(speed: number, angleRad: number): number {
  let vx = Math.cos(angleRad) * speed;
  let vy = Math.sin(angleRad) * speed;
  let x = 0, y = 0;
  const maxSteps = PROJECTILE.ARROW_LIFETIME;
  for (let i = 0; i < maxSteps; i++) {
    vy -= PROJECTILE.ARROW_GRAVITY;
    vx *= PROJECTILE.ARROW_DRAG; vy *= PROJECTILE.ARROW_DRAG;
    const ny = y + vy;
    if (ny <= 0 && vy < 0) {
      // Interpolate the crossing of y = 0 for a smooth range.
      const t = y / (y - ny);
      return x + vx * t;
    }
    x += vx; y = ny;
  }
  return x;
}
